import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../../delivery-points/constants/delivery-points-api-paths.constants';
import { DEFAULT_DELIVERY_HORIZON_DAYS } from '../constants/delivery-schedule.constants';
import { GetDeliveryPointDeliveryDaysResponse } from '../types/api-delivery-day.type';
import { DeliveryDay } from '../types/delivery-day.type';
import { toDeliveryDays } from '../utils/format-delivery-schedule.util';
import { isNotFoundError } from '../utils/is-not-found-error.util';

type UseDeliveryPointDeliveryDaysResult = {
  isLoading: boolean;
  days: DeliveryDay[];
};

type DeliveryPointDeliveryDaysState = {
  deliveryPointId: string | null;
  result: UseDeliveryPointDeliveryDaysResult;
};

const INITIAL_STATE: UseDeliveryPointDeliveryDaysResult = {
  isLoading: false,
  days: [],
};

export function useDeliveryPointDeliveryDays({
  deliveryPointId,
}: {
  deliveryPointId: string | null;
}): UseDeliveryPointDeliveryDaysResult {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<DeliveryPointDeliveryDaysState>({ deliveryPointId: null, result: INITIAL_STATE });

  useEffect(() => {
    if (!deliveryPointId) return undefined;

    let isCurrent = true;
    setState({ deliveryPointId, result: { isLoading: true, days: [] } });

    HttpClient.get<GetDeliveryPointDeliveryDaysResponse>(
      ADMIN_DELIVERY_POINTS_API_PATHS.GET_DELIVERY_DAYS(deliveryPointId),
      {
        params: { days: DEFAULT_DELIVERY_HORIZON_DAYS },
      }
    )
      .then((response) => {
        if (!isCurrent) return;
        setState({ deliveryPointId, result: { isLoading: false, days: toDeliveryDays(response.days) } });
      })
      .catch((error) => {
        if (!isCurrent) return;
        if (!isNotFoundError(error)) {
          enqueueSnackbar(
            extractTranslatedApiErrorMessage(
              error,
              'Une erreur est survenue lors de la récupération des jours de livraison'
            ),
            { variant: 'error' }
          );
        }
        setState({ deliveryPointId, result: INITIAL_STATE });
      });

    return () => {
      isCurrent = false;
    };
  }, [deliveryPointId, enqueueSnackbar]);

  if (state.deliveryPointId !== deliveryPointId) return { isLoading: !!deliveryPointId, days: [] };

  return state.result;
}
